import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useTri, EnteteTri } from './TriTableau';
import { getContrastColor } from '../../utils/planningColors';
import { libelleStatutDepuis } from '../../utils/statutAtelier';
import './SuiviAtelier.css';

// ═══════════════════════════════════════════════════════════════════════════
// Suivi atelier — une file de travail PAR statut du référentiel.
//
// Depuis le 2026-08-15 (demande utilisateur), TOUS les statuts actifs ont leur
// onglet — plus seulement quatre. Répond à deux questions dans le même écran :
// « qu'est-ce que j'ai à faire ? » pour le réceptionnaire, et « où en est-on ? »
// pour le manager, qui lit les compteurs d'un seul coup d'œil.
//
// ⚠️ SANS filtre de date, contrairement au reste du Planning : un dossier « À
// facturer » depuis avant-hier reste à facturer aujourd'hui, et c'est justement
// celui qu'on veut voir. En contrepartie le serveur BORNE chaque statut à 500
// dossiers (Restitué en compte 11 000) — le vrai total reste annoncé, et la
// troncature est dite dans la barre au lieu d'être silencieuse.
// ═══════════════════════════════════════════════════════════════════════════

// Statuts finals : plus rien à faire, exclus du badge du menu.
const STATUT_FINAL_RE = /(restitué|facturé)\s*$/i;

export default function SuiviAtelier({ onOpenIntervention, onCountChange }) {
  const [statuts, setStatuts] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [manquants, setManquants] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [ongletId, setOngletId] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [tagsActifs, setTagsActifs] = useState(() => new Set()); // multi, logique OU

  const tri = useTri(null, 'asc');               // null = ordre du serveur (le plus ancien d'abord)

  const charger = useCallback(() => {
    setChargement(true);
    api.get('/planning/suivi-atelier')
      .then(r => {
        setStatuts(r.data?.statuts || []);
        setDossiers(r.data?.dossiers || []);
        setManquants(r.data?.manquants || []);
        setErreur('');
      })
      .catch(() => setErreur('Impossible de charger le suivi atelier.'))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // L'onglet par défaut suit les données, pas une constante : si « Réceptionné »
  // venait à disparaître du référentiel, l'écran ouvrirait sur un onglet vide.
  useEffect(() => {
    if (ongletId === null && statuts.length) setOngletId(statuts[0].id);
  }, [statuts, ongletId]);

  // Le badge du menu porte le total des files ACTIVES : les statuts finals
  // (…restitué, …facturé) en sont exclus — un dossier rendu au client n'appelle
  // plus de geste d'atelier, le compter ferait un badge qui ne redescend jamais.
  const totalActif = useMemo(
    () => statuts.filter(s => !STATUT_FINAL_RE.test(s.nom)).reduce((n, s) => n + (s.count || 0), 0),
    [statuts]
  );
  useEffect(() => { if (onCountChange) onCountChange(totalActif); }, [totalActif, onCountChange]);

  const statutCourant = statuts.find(s => s.id === ongletId) || null;

  // Barre de tags : ceux réellement portés par les dossiers du suivi, avec leur
  // compte GLOBAL (tous statuts confondus) — c'est ce qui permet de découvrir
  // qu'un tag vit ailleurs que dans l'onglet courant. Les tags à zéro dossier
  // n'encombrent pas la barre.
  const tagsDisponibles = useMemo(() => {
    const parId = new Map();
    dossiers.forEach(d => (d.tags || []).forEach(t => {
      const e = parId.get(t.id) || { ...t, count: 0 };
      e.count++;
      parId.set(t.id, e);
    }));
    return [...parId.values()].sort((a, b) => b.count - a.count || a.nom.localeCompare(b.nom, 'fr'));
  }, [dossiers]);

  const basculerTag = (id) => setTagsActifs(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Filtre par tags (logique OU), transverse aux statuts.
  const porteUnTagActif = useCallback(
    (d) => tagsActifs.size === 0 || (d.tags || []).some(t => tagsActifs.has(t.id)),
    [tagsActifs]
  );

  // Sous filtre, les compteurs des onglets se recalculent : c'est la lecture
  // « OÙ sont les dossiers taggués » — la raison d'être de la barre.
  const comptesFiltres = useMemo(() => {
    if (tagsActifs.size === 0) return null;
    const m = new Map();
    dossiers.forEach(d => { if (porteUnTagActif(d)) m.set(d.statut_atelier_id, (m.get(d.statut_atelier_id) || 0) + 1); });
    return m;
  }, [dossiers, tagsActifs, porteUnTagActif]);

  const lignes = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    const nu = q.replace(/[-\s.]/g, '');
    return dossiers.filter(d => {
      if (d.statut_atelier_id !== ongletId) return false;
      if (!porteUnTagActif(d)) return false;
      if (!q) return true;
      const plaque = String(d.immat || '').toLowerCase().replace(/[-\s]/g, '');
      const libellesTags = (d.tags || []).map(t => t.nom).join(' ');
      return [d.client, d.numero_or, d.mecanicien, d.marque, d.modele, d.description, d.receptionnaire_nom, libellesTags]
        .some(v => String(v || '').toLowerCase().includes(q)) || (nu && plaque.includes(nu));
    });
  }, [dossiers, ongletId, recherche, porteUnTagActif]);

  // Accesseurs de tri : ce sur quoi on compare n'est pas toujours ce qu'on
  // affiche. L'ancienneté se trie sur l'horodatage brut et non sur « 2 h 15 »,
  // qui se comparerait alphabétiquement — « 2 jours » avant « 30 minutes ».
  const accesseurs = useMemo(() => ({
    // Le tri de la colonne Statut porte sur la DATE de passage dans le statut.
    depuis: d => (d.statut_atelier_since ? Date.parse(d.statut_atelier_since) : null),
    immat: d => d.immat,
    or: d => d.numero_or,
    receptionnaire: d => d.receptionnaire_nom,
    // Tri sur les libellés concaténés dans l'ordre d'affichage : les dossiers
    // portant les mêmes tags se regroupent, ceux sans tag se retrouvent ensemble
    // à un bout de la liste.
    tags: d => (d.tags || []).map(t => t.nom).join(', '),
  }), []);

  const triees = tri.appliquer(lignes, accesseurs);

  if (chargement && !dossiers.length) {
    return <div className="sva-etat">Chargement du suivi…</div>;
  }

  return (
    <div className="sva">
      {erreur && <div className="status-error">{erreur}</div>}
      {manquants.length > 0 && (
        <div className="status-warning sva-manquants">
          Statut introuvable dans le référentiel : {manquants.join(', ')}. Le tableau correspondant
          n'est pas affiché — vérifiez qu'il n'a pas été renommé dans Configuration → Statuts atelier.
        </div>
      )}

      {/* Les compteurs sont la vue du manager : les quatre chiffres, ensemble. */}
      <div className="sva-onglets">
        {statuts.map(s => (
          <button
            key={s.id}
            type="button"
            className={`sva-onglet${s.id === ongletId ? ' is-active' : ''}`}
            onClick={() => setOngletId(s.id)}
            style={s.id === ongletId ? { borderBottomColor: s.couleur } : undefined}
          >
            <span className="sva-pastille" style={{ background: s.couleur }} aria-hidden="true" />
            <span>{s.nom}</span>
            <span className={`sva-compteur${comptesFiltres ? ' sva-compteur--filtre' : ''}`}>
              {comptesFiltres ? (comptesFiltres.get(s.id) || 0) : s.count}
            </span>
          </button>
        ))}
      </div>

      {tagsDisponibles.length > 0 && (
        <div className="sva-tagbar" role="group" aria-label="Filtrer par tags">
          <span className="sva-tagbar-label">Tags</span>
          {tagsDisponibles.map(t => {
            const actif = tagsActifs.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                className={`sva-tagchip${actif ? ' is-active' : ''}`}
                style={actif
                  ? { background: t.couleur, borderColor: t.couleur, color: getContrastColor(t.couleur) }
                  : { color: t.couleur, borderColor: t.couleur }}
                onClick={() => basculerTag(t.id)}
                title={actif ? `Retirer le filtre « ${t.nom} »` : `Ne montrer que les dossiers « ${t.nom} »`}
              >
                {t.nom} <span className="sva-tagchip-n">{t.count}</span>
              </button>
            );
          })}
          {tagsActifs.size > 0 && (
            <button type="button" className="sva-tagbar-clear" onClick={() => setTagsActifs(new Set())}>
              ✕ Effacer le filtre
            </button>
          )}
        </div>
      )}

      <div className="page-toolbar sva-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
        />
        <span className="sva-resume">
          {triees.length} dossier{triees.length > 1 ? 's' : ''}
          {recherche && dossiers.filter(d => d.statut_atelier_id === ongletId).length !== triees.length
            && ` sur ${dossiers.filter(d => d.statut_atelier_id === ongletId).length}`}
          {/* La borne serveur, dite en clair : 500 affichés sur 11 101. */}
          {!recherche && statutCourant && statutCourant.count > dossiers.filter(d => d.statut_atelier_id === ongletId).length && (
            <span className="sva-borne"> — les {dossiers.filter(d => d.statut_atelier_id === ongletId).length} {STATUT_FINAL_RE.test(statutCourant.nom) ? 'plus récents' : 'plus anciens'} sur {statutCourant.count}</span>
          )}
        </span>
        <button type="button" className="page-header-btn sva-actualiser" onClick={charger} title="Actualiser">↻</button>
      </div>

      {triees.length === 0 ? (
        <div className="sva-etat">
          {recherche
            ? <>Aucun dossier ne correspond à « {recherche} ».</>
            : tagsActifs.size > 0
              ? <>Aucun dossier « {statutCourant?.nom || '—'} » ne porte {tagsActifs.size > 1 ? 'ces tags' : 'ce tag'} — les compteurs des autres statuts indiquent où ils sont.</>
              : <>Aucun dossier en « {statutCourant?.nom || '—'} ». Rien à faire ici.</>}
        </div>
      ) : (
        <div className="table-container">
          <table className="sva-table">
            <thead>
              <tr>
                {/* Colonnes réduites le 2026-08-15 (demande utilisateur) : Date
                    RDV, Client et Mécanicien sont partis — le tableau tient dans
                    la largeur de page sans défilement. Le tri de « Statut »
                    porte sur la DATE de passage dans le statut. */}
                <EnteteTri tri={tri} cle="depuis" title="Trier par date de passage dans le statut">Statut</EnteteTri>
                <EnteteTri tri={tri} cle="immat">Immat.</EnteteTri>
                <EnteteTri tri={tri} cle="or">N° OR</EnteteTri>
                <EnteteTri tri={tri} cle="receptionnaire">Réceptionnaire</EnteteTri>
                <EnteteTri tri={tri} cle="tags">Tags</EnteteTri>
              </tr>
            </thead>
            <tbody>
              {triees.map(d => {
                const libelle = libelleStatutDepuis(statutCourant?.nom, d.statut_atelier_since, d.statut_atelier_since_estime);
                return (
                  <tr key={d.id} onClick={() => onOpenIntervention && onOpenIntervention(d)} className="sva-ligne">
                    <td className="sva-statut-cell">
                      {/* « Réceptionné depuis le 11/08 » : la pastille du statut
                          au vocabulaire partagé (utils/statutAtelier). La durée
                          exacte reste en infobulle. */}
                      <span className="sva-statut-pill"
                        style={{ background: statutCourant?.couleur || 'var(--bg-muted)', color: statutCourant?.text_color || getContrastColor(statutCourant?.couleur || '#ccc') }}
                        title={d.statut_atelier_since_estime
                          ? "Date approchée : ce dossier était déjà dans ce statut avant la mise en service du suivi."
                          : (d.statut_atelier_since ? new Date(d.statut_atelier_since).toLocaleString('fr-FR') : '')}
                      >
                        {statutCourant?.nom}{libelle ? ` ${libelle}` : ''}
                      </span>
                    </td>
                    <td className="sva-immat">{d.immat || '—'}</td>
                    <td className="sva-or">{d.numero_or || '—'}</td>
                    <td>{d.receptionnaire_nom || '—'}</td>
                    <td>
                      {d.tags?.length ? (
                        <span className="sva-tags">
                          {d.tags.map(t => (
                            <span key={t.id} className="sva-tag"
                              style={{ background: t.couleur, color: getContrastColor(t.couleur) }}>{t.nom}</span>
                          ))}
                        </span>
                      ) : <span className="sva-vide">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
